import { useEffect, useLayoutEffect, useState, useRef } from "react"
import { createPortal } from "react-dom"
import { mvSubscribe } from "./utils"
import { useConfigurator, writePricePolicy } from "./context"
import Configurator from "./index"
import { useLocale } from "contexts/Locale"
import { useUser } from "contexts/User"
import { useNavigation } from "contexts/Navigation"
import { useDictionary } from "contexts/Dictionary"
import { useMotionValue, useScroll } from "framer-motion"
import styled from "@emotion/styled"
import getMediaQuery from "css/breakpoints"
import { visuallyHidden, headline30, legend100 } from "css/text"
import { buttonReset, buttonIcon, translucentLight } from "css/buttons"
import { Icon } from "components/icon/Icon"
import { hashIt } from "./hash"
import { usePanelModal } from "contexts/PanelModal"
import { fullGrid } from "css/grid"
import P13N from "./legal"

const Close = styled.button`
  ${buttonIcon}
  ${translucentLight}
//  --spacing: calc(var(--outer-margin) - var(--height) / 2);

  --spacing: calc(var(--outer-margin) - var(--grid-gap) / 2 - var(--height) / 2);

  ${getMediaQuery("m")} {
//    --spacing: 3.125rem;
    --spacing: calc(var(--outer-margin) / 2 - var(--height) / 2);
  }
  inset-block-start: var(--spacing);
  inset-inline-end: var(--spacing);
  position: absolute;
  z-index: 29;
  animation-name: close-reveal;
  animation-fill-mode: both;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.61, 1, 0.88, 1);
  animation-delay: 0ms;

  & > svg {
    fill: rgb(var(--light-black));
  }

  @media (hover: hover) {
    :hover {
      --container: rgb(243 243 243 / 0.5) !important;
      --text: rgb(var(--light-black));
    }
  }

  @keyframes close-reveal {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

export default function ConfiguratorPlayer({ initialState }) {
  const [active, setActive] = useState(null)
  const { route, fromui, launched, cta, page_type } = useConfigurator()
  const locale = useLocale()
  const dictionary = useDictionary()
  const { panelModalState } = usePanelModal()

  function kill() {
    if (fromui.get()) hashIt("")
    setTimeout(() => {
      route.set(null)
    }, 10)
  }

  function onClick(e) {
    fromui.set(true)
    kill()
  }

  function onRoute(v) {
    //    console.log("ConfiguratorPlayer onRoute", v)
    let [family, startrmc, startstep] = (v || "").split("/").filter(v => !!v?.length)
    //    console.log("ConfiguratorPlayer onRoute split family, startrmc, startstep", family, startrmc, startstep)
    const hash = window.location.hash.split("").slice(1).join("")
    panelModalState.set(!!v)
    //    console.log("ConfiguratorPlayer onRoute hash", hash)
    if (hash.length) {
      const [cfg, ...all] = hash.split("/")
      if (cfg === "cfg") {
        //        console.log("ConfiguratorPlayer onRoute hash length & valid")
        launched.set(true)
        if (!v) return route.set(all.join("/"))
        else v = all.join("/")
      }
    }
    //    console.log("ConfiguratorPlayer onRoute after hash", v)
    if (!v) {
      //      console.log("ConfiguratorPlayer onRoute no route", v)
      launched.set(false)
      fromui.set(false)
      if (cta.current) cta.current.focus()

      return setActive(null)
    }
    ;[family, startrmc, startstep] = v.split("/").filter(v => !!v?.length)
    const credentials = initialState?.env?.withCredentials ? "include" : "same-origin"
    const istate = initialState[locale.current.codes.www]
    const families = istate?.configurator?.[0]?.families
    const f = families?.find(f => f.family_code === family)
    const intro = f?.intro?.[0]
    const color_set = f?.params?.[0]?.color_set
    startrmc = startrmc || f?.params?.[0]?.rmc

    const cfgprops = {
      family: family,
      startrmc: startrmc,
      startstep: startstep,
      bkgmode: true,
      fromui: fromui,
      launched: launched,
      route: route,
      page_type: page_type,
      clear: kill,
      onClose: onClick,
      intro: intro,
      color_set: color_set ? `page-color-set-rolex-v7-color-${color_set}` : null,
      //      className: startstep ? "wv_straight" : null,
      credentials: credentials,
    }

    setActive(
      <>
        <Configurator {...cfgprops}>
          <Close className='wv_reveal dark-theme' onClick={onClick} aria-label={dictionary.popinClose()}>
            <Icon type='close' />
          </Close>
          <P13N legal_disclaimer={istate?.configurator?.[0]?.legal_disclaimer} />
        </Configurator>
      </>
    )
  }
  useLayoutEffect(() => mvSubscribe(route, onRoute), [])

  useEffect(() => kill, [])

  return process.browser && active ? createPortal(active, document.body) : null
}
